<template>
    <UApp :locale="uiLocale">
        <UTheme :props="uiDefaults">
            <component :is="layout">
                <router-view />
            </component>
        </UTheme>
    </UApp>
</template>

<script lang="ts" setup>
    import * as locales from '@nuxt/ui/locale'
    import { computed, onMounted, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute } from 'vue-router'

    import Blank   from '@/layouts/Blank.vue'
    import Default from '@/layouts/Default.vue'

    import { useGroupTree } from '@/libs/groupTree'
    import { useScopeQuery } from '@/libs/scopeQuery'
    import { uiDefaults } from '@/libs/uiDefaults'
    import { useAppStore } from '@/stores/app'

    const route = useRoute()
    const { locale } = useI18n()
    const appStore   = useAppStore()

    const { loadTree } = useGroupTree()
    const scopeQuery   = useScopeQuery()

    // Keeps Nuxt UI's built-in component texts in sync with the vue-i18n locale.
    const uiLocale = computed(() => (locales as Record<string, typeof locales.en>)[locale.value] ?? locales.en)

    const layouts = { Default, Blank }
    const layout  = computed(() => layouts[(route.meta.layout as keyof typeof layouts) ?? 'Default'])

    /**
     * A reload does not go through the sign-in screen, so the account is read here as
     * well — and with it the group, which the scope selector in the top bar is drawn
     * from. Without the second read the console would know it has a token and nothing
     * about which houses there are to choose between.
     */
    const start = async () => {
        // The address decides the scope BEFORE the tree arrives: the store drops a
        // selection it cannot find in the tree, and a link into a region that was read
        // afterwards would be dropped for not being in a tree nobody had read yet.
        scopeQuery.start()

        await appStore.ensureUserData()
        await loadTree()
    }

    // Signing in happens on a screen that has no top bar, so the group has to be read
    // when the token appears rather than only when the page loads.
    watch(() => appStore.JWT, (jwt) => { if (jwt) void loadTree() })

    onMounted(() => { void start() })
</script>
